import React, { useState } from 'react'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { HStack } from '@astryxdesign/core/HStack'
import PillButton from './PillButton'
import { IconButton } from '@astryxdesign/core/IconButton'
import { MessageSquarePlus, GitCompare } from 'lucide-react'
import { marked } from 'marked'
import { createId } from '../lib/id'
import { splitStoryParagraphs } from '../lib/storyEditFeedback'
import { buildDiffRenderItems, computeLineDiff, type DiffLine } from '../utils/lineDiff'
import {
  ReviewCommentChip,
  ReviewCommentEditor,
  WHOLE_REVIEW_COMMENT_LINE_INDEX,
  type ReviewComment
} from './ReviewComments'

interface StoryEditModalProps {
  content: string
  comments?: ReviewComment[]
  // The block this one revised, when it answers an edit request — enables the diff view.
  previousContent?: string
  // Once the conversation has moved on (the block's Insert button is gone), the block is only
  // viewable: comments it already carries are shown, but no new ones can be added or sent.
  isReadOnly?: boolean
  onRequestChanges: (comments: ReviewComment[]) => void
  onCommentsChange: (comments: ReviewComment[]) => void
  onClose: () => void
}

/**
 * The story-block counterpart of PlanModal: the block split into paragraphs the writer can
 * comment on, a whole-passage comment, and a diff against the block this one revised. Shares
 * the plan modal's CSS so the two feel like one feature.
 */
export default function StoryEditModal({
  content,
  comments: initialComments,
  previousContent,
  isReadOnly = false,
  onRequestChanges,
  onCommentsChange,
  onClose
}: StoryEditModalProps): React.JSX.Element {
  const [comments, setComments] = useState<ReviewComment[]>(initialComments || [])
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [draftComment, setDraftComment] = useState('')
  const [showDiff, setShowDiff] = useState(false)

  const paragraphs = splitStoryParagraphs(content)
  const canShowDiff = Boolean(previousContent && previousContent.trim() !== content.trim())
  const diffItems = canShowDiff
    ? buildDiffRenderItems(computeLineDiff(splitStoryParagraphs(previousContent!), paragraphs))
    : []

  const commentFor = (index: number): ReviewComment | undefined =>
    comments.find((comment) => comment.lineIndex === index)
  const wholeComment = commentFor(WHOLE_REVIEW_COMMENT_LINE_INDEX)
  const hasUnresolved = comments.some((comment) => !comment.resolved)

  const updateComments = (next: ReviewComment[]): void => {
    setComments(next)
    onCommentsChange(next)
  }

  const handleSaveComment = (index: number, lineText: string): void => {
    const text = draftComment.trim()
    if (!text) return
    const existing = commentFor(index)
    updateComments(
      existing
        ? comments.map((comment) =>
            comment.lineIndex === index ? { ...comment, comment: text, resolved: false } : comment
          )
        : [...comments, { id: createId(), lineIndex: index, lineText, comment: text }]
    )
    setDraftComment('')
    setActiveIndex(null)
  }

  const handleRemoveComment = (index: number): void => {
    updateComments(comments.filter((comment) => comment.lineIndex !== index))
  }

  const openEditor = (index: number, initial: string): void => {
    setActiveIndex(index)
    setDraftComment(initial)
  }
  const closeEditor = (): void => {
    setActiveIndex(null)
    setDraftComment('')
  }

  const renderWordRuns = (words: DiffLine[], highlight: 'added' | 'removed'): React.ReactNode => {
    const runs: DiffLine[] = []
    for (const word of words) {
      const last = runs[runs.length - 1]
      if (last && last.type === word.type) last.text += word.text
      else runs.push({ type: word.type, text: word.text })
    }
    return runs.map((run, index) => (
      <span
        key={index}
        className={run.type === highlight ? `plan-modal-diff-word-${highlight}` : undefined}
        dangerouslySetInnerHTML={{ __html: marked.parseInline(run.text) as string }}
      />
    ))
  }

  const renderInline = (text: string): { __html: string } => ({
    __html: marked.parseInline(text) as string
  })

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => !open && onClose()}
      variant="standard"
      purpose="form"
      width="min(80vw, 1000px)"
      maxHeight="80vh"
    >
      <DialogHeader
        title={isReadOnly ? 'Passage' : 'Edit passage'}
        subtitle={
          isReadOnly
            ? 'This passage is from earlier in the conversation and can no longer be edited.'
            : 'Comment on a paragraph, or on the whole passage, and request changes.'
        }
        onOpenChange={() => onClose()}
      />
      <div className="plan-modal-scroll">
        <div className="plan-modal-container">
          {showDiff ? (
            <div className="plan-modal-diff">
              {diffItems.map((item, index) => {
                if (item.kind === 'modified') {
                  return (
                    <div key={index} className="plan-modal-diff-modified-pair">
                      <div className="plan-modal-diff-line plan-modal-diff-line-modified-old story-edit-paragraph">
                        {renderWordRuns(item.oldWords, 'removed')}
                      </div>
                      <div className="plan-modal-diff-line plan-modal-diff-line-modified-new story-edit-paragraph">
                        {renderWordRuns(item.newWords, 'added')}
                      </div>
                    </div>
                  )
                }
                return (
                  <div
                    key={index}
                    className={`plan-modal-diff-line plan-modal-diff-line-${item.kind} story-edit-paragraph`}
                    dangerouslySetInnerHTML={renderInline(item.text)}
                  />
                )
              })}
            </div>
          ) : (
            <div className="plan-modal-lines">
              {(!isReadOnly || wholeComment) && (
                <div className="plan-modal-whole-plan-row">
                  {isReadOnly ? null : wholeComment ? (
                    <PillButton
                      variant="ghost"
                      size="sm"
                      label="Edit"
                      endContent={<MessageSquarePlus size={12} />}
                      tooltip="Edit comment"
                      onClick={() =>
                        openEditor(WHOLE_REVIEW_COMMENT_LINE_INDEX, wholeComment.comment)
                      }
                      className="plan-modal-comment-trigger plan-modal-comment-trigger-active"
                    />
                  ) : (
                    activeIndex !== WHOLE_REVIEW_COMMENT_LINE_INDEX && (
                      <PillButton
                        variant="ghost"
                        size="sm"
                        label="Comment on the whole passage"
                        icon={<MessageSquarePlus size={12} />}
                        tooltip="Comment on the whole passage"
                        onClick={() => openEditor(WHOLE_REVIEW_COMMENT_LINE_INDEX, '')}
                        className="plan-modal-whole-plan-trigger"
                      />
                    )
                  )}
                  {wholeComment && activeIndex !== WHOLE_REVIEW_COMMENT_LINE_INDEX && (
                    <ReviewCommentChip
                      comment={wholeComment}
                      isReadOnly={isReadOnly}
                      onEdit={() =>
                        openEditor(WHOLE_REVIEW_COMMENT_LINE_INDEX, wholeComment.comment)
                      }
                      onRemove={() => handleRemoveComment(WHOLE_REVIEW_COMMENT_LINE_INDEX)}
                      className="plan-modal-whole-plan-chip"
                    />
                  )}
                  {!isReadOnly && activeIndex === WHOLE_REVIEW_COMMENT_LINE_INDEX && (
                    <ReviewCommentEditor
                      value={draftComment}
                      onChange={setDraftComment}
                      placeholder="What should change about the passage overall?"
                      onSave={() => handleSaveComment(WHOLE_REVIEW_COMMENT_LINE_INDEX, '')}
                      onCancel={closeEditor}
                    />
                  )}
                </div>
              )}
              {paragraphs.map((paragraph, index) => {
                const comment = commentFor(index)
                return (
                  <div key={index} className="plan-modal-line-row story-edit-paragraph-row">
                    <div className="plan-modal-line-header">
                      <div
                        className="plan-modal-line-text story-edit-paragraph"
                        dangerouslySetInnerHTML={renderInline(paragraph)}
                      />
                      {isReadOnly ? null : comment ? (
                        <PillButton
                          variant="ghost"
                          size="sm"
                          label="Edit"
                          endContent={<MessageSquarePlus size={12} />}
                          tooltip="Edit comment"
                          onClick={() => openEditor(index, comment.comment)}
                          className="plan-modal-comment-trigger plan-modal-comment-trigger-active"
                        />
                      ) : (
                        <IconButton
                          variant="ghost"
                          size="sm"
                          icon={<MessageSquarePlus size={12} />}
                          label="Comment on this paragraph"
                          tooltip="Comment on this paragraph"
                          onClick={() => openEditor(index, '')}
                          className="plan-modal-comment-trigger"
                        />
                      )}
                    </div>
                    {comment && activeIndex !== index && (
                      <ReviewCommentChip
                        comment={comment}
                        isReadOnly={isReadOnly}
                        onEdit={() => openEditor(index, comment.comment)}
                        onRemove={() => handleRemoveComment(index)}
                      />
                    )}
                    {!isReadOnly && activeIndex === index && (
                      <ReviewCommentEditor
                        value={draftComment}
                        onChange={setDraftComment}
                        placeholder="What should change in this paragraph?"
                        onSave={() => handleSaveComment(index, paragraph)}
                        onCancel={closeEditor}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
      <div className="plan-modal-footer">
        <HStack gap={1} justify="between">
          <div>
            {canShowDiff && (
              <PillButton
                variant={showDiff ? 'secondary' : 'ghost'}
                size="sm"
                label={showDiff ? 'Hide Diff' : 'Show Diff'}
                icon={<GitCompare size={12} />}
                onClick={() => setShowDiff((value) => !value)}
              />
            )}
          </div>
          <HStack gap={1} justify="end">
            <PillButton variant="ghost" label="Close" onClick={onClose} />
            {!isReadOnly && (
              <PillButton
                variant="primary"
                label="Request Changes"
                isDisabled={!hasUnresolved}
                onClick={() => onRequestChanges(comments)}
              />
            )}
          </HStack>
        </HStack>
      </div>
    </Dialog>
  )
}
